import { FormInstance } from "antd";
import * as commonAPI from "../../../../services/commonServices";
import { CustomNotification } from "../../../../util/CustomNotification";
import { useEffect, useState } from "react";
import { KeyFobList } from "../../../../shared/interface/MasterManagement/keyFobMaster.interface";
import useValue from "../../../../hooks/useValue";
import { useSelector } from "react-redux";
import { RootState } from "../../../../appRedux/store";
import * as advanceBookingRequestService from "../../../../services/advanceBookingRequestService";

interface useKeyFobAllocationProps {
  form: FormInstance<any>;
  advance_booking_request_id: number;
  vehicle_type_id: number;
  booking_id: string;
}

export const useKeyFobAllocation = (props: useKeyFobAllocationProps) => {
  /* CONSTANTS */

  /* STATE */
  const [keyFobList, setKeyFobList] = useState<KeyFobList[]>([]);

  /* HOOKS */
  const { value: loading, handleDisableValue: disableLoading, handleEnableValue: enableLoading } = useValue(true);
  const { masterFilters } = useSelector(({ common }: RootState) => common);
  const { customerId, locationId } = masterFilters ?? {};

  /* HELPER FUNCTIONS */
  const fetchKeyFobList = async (vehicleTypeId: number) => {
    try {
      if (!vehicleTypeId) return;
      enableLoading();

      const response = await commonAPI.getUnallocatedKeyFobListByCustomerAndLocationId(customerId!, locationId!, vehicleTypeId);

      const formattedResponse = response?.data?.map((item) => ({
        ...item,
        keyFob_vehicle_dimension: `${item?.key_fob_token ?? ""} - ${item?.vehicleType?.vehicle_type_name ?? ""} - ${item?.vehicleType?.dimension ?? ""}`
      }));

      setKeyFobList(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      disableLoading();
    }
  };

  const fetchAbrDetails = async (abrId: number) => {
    try {
      if (!abrId) return;
      const response = await advanceBookingRequestService.getAbrDetailsAPI(abrId);

      const formData = response?.data;
      props.form.setFieldsValue({
        key_fob_id: formData?.keyFobAllocation?.key_fob_id,
        vehicle_number: formData?.keyFobAllocation?.vehicle_number,
        booking_id: formData?.booking_id
      });

      setKeyFobList((prev: any) => [
        ...prev,
        {
          id: formData?.keyFobAllocation?.keyFob?.id,
          key_fob_token: formData?.keyFobAllocation?.keyFob?.key_fob_token,
          vehicleType: {
            id: formData?.vehicleType?.id,
            vehicle_type_name: formData?.vehicleType?.vehicle_type_name,
            dimension: formData?.vehicleType?.dimension
          },
          keyFob_vehicle_dimension: `${formData?.keyFobAllocation?.keyFob?.key_fob_token ?? ""} - ${formData?.vehicleType?.vehicle_type_name ?? ""} - ${formData?.vehicleType?.dimension ?? ""}`
        }
      ]);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  /* HANDLERS */

  /* EFFECTS */
  useEffect(() => {
    const fetchDataAndUpdateFormValue = async () => {
      try {
        enableLoading();

        await fetchAbrDetails(props?.advance_booking_request_id);
        await fetchKeyFobList(props?.vehicle_type_id);

        props.form.setFieldsValue({
          advance_booking_request_id: props?.advance_booking_request_id,
          booking_id: props?.booking_id
        });
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        disableLoading();
      }
    };

    fetchDataAndUpdateFormValue();
  }, []);

  return { loading, keyFobList };
};
